import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Box,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  useColorModeValue,
  Flex,
  Progress,
  Skeleton,
  Card,
  CardBody
} from '@chakra-ui/react';
import { 
  MdBarChart, 
  MdSearch, 
  MdSettings,
  MdClose 
} from 'react-icons/md';
import { formatDateTime } from '../../utils/helpers';

// ClientStatsPopup Component
function ClientStatsPopup({ 
  isOpen, 
  onClose, 
  clientName, 
  clientId, 
  statsData,
  updatedTime,
  isLoading = false
}) {
  const [searchTerm, setSearchTerm] = useState('');

  // Theme-aware colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('linear(to-r, blue.500, blue.600)', 'linear(to-r, blue.600, blue.700)');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const footerBg = useColorModeValue('gray.50', 'gray.700');
  const progressBg = useColorModeValue('gray.100', 'gray.600');

  if (!statsData || statsData.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(8px)" />
        <ModalContent bg={bgColor} borderRadius="xl" boxShadow="2xl">
          <ModalHeader bgGradient={headerBg} color="white" borderTopRadius="xl">
            <HStack>
              <Icon as={MdBarChart} boxSize={5} />
              <Text fontWeight="600">Click Statistics - {clientName}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />
          <ModalBody p={6}>
            <VStack spacing={4}>
              <Icon as={MdBarChart} boxSize={12} color={mutedTextColor} />
              <Text textAlign="center" color={mutedTextColor} fontSize="lg">
                No statistics data available for this client.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter bg={footerBg} borderBottomRadius="xl">
            <Button colorScheme="blue" onClick={onClose} borderRadius="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  // Calculate summary statistics
  const totalClicks = statsData.reduce((sum, item) => sum + item.data_id_count, 0);
  const avgClicks = Math.round(totalClicks / statsData.length);
  const maxClicks = Math.max(...statsData.map(item => item.data_id_count));

  // Filter data based on search term
  const filteredData = statsData.filter(item =>
    item.data_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Categorize actions
  const getActionType = (dataId) => {
    if (dataId.startsWith('Action:')) return 'action';
    if (dataId.startsWith('search_')) return 'search';
    if (dataId.startsWith('dash_')) return 'dashboard';
    return 'other';
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'action': return 'blue';
      case 'search': return 'green';
      case 'dashboard': return 'purple';
      default: return 'gray';
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'action': return MdSettings;
      case 'search': return MdSearch;
      case 'dashboard': return MdBarChart;
      default: return MdSettings;
    }
  };

  const StatCard = ({ label, value, color, icon }) => (
    <Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="xl" overflow="hidden">
      <CardBody p={4}>
        <VStack align="start" spacing={2}>
          <HStack justify="space-between" w="full">
            <Box 
              bg={`${color}.100`} 
              p={2} 
              borderRadius="lg"
              _dark={{ bg: `${color}.800` }}
            >
              <Icon as={icon} color={`${color}.600`} boxSize={4} />
            </Box>
            <Badge 
              colorScheme={color} 
              variant="subtle" 
              borderRadius="full" 
              px={2} 
              fontSize="xs"
            >
              Analytics
            </Badge>
          </HStack>
          <Box>
            <Text fontSize="xs" color={mutedTextColor} fontWeight="500" mb={1}>
              {label}
            </Text>
            <Text fontSize="xl" fontWeight="700" color={textColor} lineHeight={1}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Text>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(8px)" />
      <ModalContent maxH="95vh" bg={bgColor} borderRadius="2xl" boxShadow="2xl" mx={4}>
        <ModalHeader 
          bgGradient={headerBg} 
          color="white" 
          borderTopRadius="2xl" 
          py={6}
          position="relative"
        >
          <HStack spacing={4}>
            <Box 
              bg="whiteAlpha.200" 
              p={2} 
              borderRadius="lg"
              backdropFilter="blur(10px)"
            >
              <Icon as={MdBarChart} boxSize={6} />
            </Box>
            <VStack align="start" spacing={1}>
              <Text fontWeight="600" fontSize="xl">
                Click Analytics - {clientName}
              </Text>
              <HStack spacing={3}>
                <Badge 
                  bg="whiteAlpha.200" 
                  color="white" 
                  borderRadius="full" 
                  px={3} 
                  py={1}
                  fontSize="xs"
                  fontWeight="500"
                >
                  ID: {clientId}
                </Badge>
                <Badge 
                  bg="whiteAlpha.300" 
                  color="white" 
                  borderRadius="full" 
                  px={3} 
                  py={1}
                  fontSize="xs"
                  fontWeight="500"
                >
                  📅 24hr Data
                </Badge>
              </HStack>
            </VStack>
          </HStack>
          <ModalCloseButton 
            color="white" 
            _hover={{ bg: 'whiteAlpha.200' }} 
            borderRadius="lg"
            size="lg"
          />
        </ModalHeader>
        
        <ModalBody p={8}>
          <VStack spacing={6} align="stretch">
            {/* Summary Stats */}
            <Box>
              <HStack justify="space-between" align="center" mb={3}>
                <Text fontSize="md" fontWeight="700" color={textColor}>
                  📊Summary
                </Text>
                <Badge 
                  colorScheme="orange" 
                  variant="solid" 
                  borderRadius="full" 
                  px={3}
                  fontSize="xs"
                  fontWeight="600"
                >
                  🕒 24hr Period
                </Badge>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <StatCard
                  label="Total Clicks"
                  value={totalClicks}
                  color="blue"
                  icon={MdBarChart}
                />
                <StatCard
                  label="Average Clicks"
                  value={avgClicks}
                  color="green"
                  icon={MdBarChart}
                />
                <StatCard
                  label="Peak Activity"
                  value={maxClicks}
                  color="purple"
                  icon={MdBarChart}
                />
              </SimpleGrid>
            </Box>

            <Divider borderColor={borderColor} />

            {/* Search Filter */}
            <Box>
              <Text fontSize="md" fontWeight="700" color={textColor} mb={3}>
                🔍 Search & Filter
              </Text>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" h="full">
                  <Icon as={MdSearch} color={mutedTextColor} boxSize={4} />
                </InputLeftElement>
                <Input
                  placeholder="Type to filter data IDs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg={inputBg}
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                  fontSize="sm"
                  _hover={{ borderColor: "blue.300" }}
                  _focus={{ 
                    borderColor: "blue.500", 
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                  }}
                />
              </InputGroup>
            </Box>

            {/* Data Table */}
            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="md" fontWeight="700" color={textColor}>
                  📈 Analytics Data
                </Text>
                <Badge 
                  colorScheme="blue" 
                  variant="subtle" 
                  borderRadius="full" 
                  px={3}
                  fontSize="xs"
                >
                  {filteredData.length} entries
                </Badge>
              </Flex>
              
              <Card borderRadius="lg" overflow="hidden" border="1px" borderColor={borderColor}>
                <TableContainer maxH="400px" overflowY="auto">
                  <Table variant="simple" size="sm" bg={tableBg}>
                    <Thead bg={tableHeaderBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th 
                          borderColor={borderColor} 
                          color={textColor} 
                          fontWeight="600"
                          fontSize="xs"
                          py={3}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Type
                        </Th>
                        <Th 
                          borderColor={borderColor} 
                          color={textColor} 
                          fontWeight="600"
                          fontSize="xs"
                          py={3}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Data ID
                        </Th>
                        <Th 
                          isNumeric 
                          borderColor={borderColor} 
                          color={textColor} 
                          fontWeight="600"
                          fontSize="xs"
                          py={3}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Clicks
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredData.map((item, index) => {
                        const actionType = getActionType(item.data_id);
                        
                        return (
                          <Tr 
                            key={index} 
                            _hover={{ bg: hoverBg }}
                            transition="all 0.2s"
                          >
                            <Td borderColor={borderColor} py={3}>
                              <HStack spacing={2}>
                                <Box
                                  bg={`${getActionColor(actionType)}.100`}
                                  p={1.5}
                                  borderRadius="md"
                                  _dark={{ bg: `${getActionColor(actionType)}.800` }}
                                >
                                  <Icon 
                                    as={getActionIcon(actionType)} 
                                    color={`${getActionColor(actionType)}.600`}
                                    boxSize={3}
                                  />
                                </Box>
                                <Badge 
                                  colorScheme={getActionColor(actionType)} 
                                  variant="subtle"
                                  borderRadius="md"
                                  px={2}
                                  fontSize="xs"
                                  fontWeight="500"
                                  textTransform="capitalize"
                                >
                                  {actionType}
                                </Badge>
                              </HStack>
                            </Td>
                            <Td borderColor={borderColor} py={3}>
                              <Text 
                                fontSize="xs" 
                                fontFamily="mono" 
                                color={textColor}
                                fontWeight="500"
                                noOfLines={1}
                              >
                                {item.data_id}
                              </Text>
                            </Td>
                            <Td isNumeric borderColor={borderColor} py={3}>
                              <Badge
                                colorScheme="blue"
                                variant="solid"
                                borderRadius="full"
                                px={2}
                                fontSize="xs"
                                fontWeight="600"
                              >
                                {item.data_id_count.toLocaleString()}
                              </Badge>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Card>

              {filteredData.length === 0 && (
                <Card mt={4} bg={cardBg} border="1px" borderColor={borderColor} borderRadius="xl">
                  <CardBody py={12}>
                    <VStack spacing={4}>
                      <Icon as={MdSearch} boxSize={12} color={mutedTextColor} />
                      <Text color={mutedTextColor} fontSize="lg" textAlign="center">
                        No results found for "{searchTerm}"
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearchTerm('')}
                        colorScheme="blue"
                      >
                        Clear search
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg={footerBg} borderBottomRadius="2xl" py={6}>
          <HStack spacing={4} w="full" justify="space-between">
            <Text fontSize="sm" color={mutedTextColor} fontWeight="500">
              Last updated: {formatDateTime(updatedTime)}
            </Text>
            <Button 
              colorScheme="blue" 
              onClick={onClose}
              borderRadius="lg"
              px={8}
              fontWeight="600"
              leftIcon={<Icon as={MdClose} />}
            >
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ClientStatsPopup;